import type { ExperimentUser } from '@amplitude/experiment-js-client';
import type { UserProperties } from './remoteFeatureFlags.types';

/**
 * Cookie names used to resolve the Amplitude device_id, in priority order.
 *
 * Shared between the SSR fetch path ([fetchAmplitudeFlagsForUser.ts](src/server/middleware/serverRenderer/fetchAmplitudeFlagsForUser.ts))
 * and the client-side exposure tracker ([amplitudeExposureTracking.ts](src/shared/core/featureFlags/remoteFeatureFlags/amplitudeExposureTracking.ts))
 * so that both code paths agree on which identity to use. If this order changes here, both consumers
 * automatically pick it up — eliminating drift between bucketing-time identity (SSR) and
 * exposure-time identity (client).
 *
 * Order rationale: `joy_anonymous_id` is set by Joy's own CookieStorage and is treated as the
 * canonical anonymous id. `ajs_anonymous_id` is Segment's cookie, used as a backwards-compat fallback
 * for visitors whose Joy cookie hasn't been set yet.
 */
export const AMPLITUDE_DEVICE_ID_COOKIE_NAMES = ['joy_anonymous_id', 'ajs_anonymous_id'] as const;

/**
 * Input for building an Amplitude ExperimentUser
 *
 * Separates event-specific fields (eventFirebaseId, eventId) from generic userProperties
 * to ensure consistent handling of these commonly-used targeting properties.
 */
export type BuildExperimentUserInput = Readonly<{
  userId: string | undefined;
  deviceId: string | undefined;
  eventFirebaseId: string | undefined;
  eventId: string | undefined;
  userProperties?: UserProperties;
}>;

/**
 * Builds an Amplitude ExperimentUser object with proper userProperties handling
 *
 * This utility provides a shared transformation layer between our internal user context
 * and the Amplitude SDK's ExperimentUser type.
 *
 * Key behaviors:
 * - Automatically adds `lastFirebaseEventId` to userProperties if eventFirebaseId is provided
 * - Automatically adds `eventId` to userProperties if eventId is provided
 * - Merges provided userProperties with the auto-added properties
 *
 * Used by:
 * - Client-side: remoteFeatureFlags.tsx
 * - Server-side: fetchAmplitudeFlagsForUser.ts (for SSR feature flag fetching)
 */
export function buildExperimentUser(input: BuildExperimentUserInput): ExperimentUser {
  const { userId, deviceId, eventFirebaseId, eventId, userProperties } = input;

  // Build user_properties with event-specific fields
  const mergedUserProperties: UserProperties = {
    ...userProperties,
  };

  // Add lastFirebaseEventId if available (used for Amplitude targeting segments)
  if (eventFirebaseId) {
    mergedUserProperties.lastFirebaseEventId = eventFirebaseId;
  }

  // Add eventId if available (used for Amplitude targeting segments)
  if (eventId) {
    mergedUserProperties.eventId = eventId;
  }

  // Only include user_properties if we have any properties to send
  const hasUserProperties = Object.keys(mergedUserProperties).length > 0;

  return {
    user_id: userId,
    device_id: deviceId,
    groups: {
      JoyEvent: eventFirebaseId ? [eventFirebaseId] : [],
    },
    user_properties: hasUserProperties ? mergedUserProperties : undefined,
  };
}
